import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { databases, databaseId, collectionId } from '@/lib/appwrite';
import { Query } from 'appwrite';

export interface Product {
  $id: string;
  name: string;
  slug: string;
  type: string;
  featured: boolean;
  image_url?: string;
  description?: string;
  fragrantica_description?: string;
  categories: {
    $id: string;
    slug?: string;
    name: string;
    description: string;
  }[];
  // Campos enriquecidos desde Fragrantica
  gender?: 'female' | 'male' | 'unisex';
  fragrantica_image?: string;
  fragrantica_brand?: string;
  fragrantica_year?: string;
  fragrantica_rating?: string;
  fragrantica_url?: string;
  fragrantica_id?: string;
  $createdAt: string;
  $updatedAt: string;
}

const shuffleArray = <T,>(array: T[]): T[] => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

export const useFeaturedProducts = (limit: number = 4) => {
  return useQuery({
    queryKey: ['products', limit],
    queryFn: async () => {
      const response = await databases.listDocuments(
        databaseId,
        collectionId,
        [
          Query.equal('featured', true),
          Query.limit(500),
          Query.select(['*', 'categories.*']), // Cambiado a categories.*
        ] as string[]
      );
      const shuffled = shuffleArray(response.documents as unknown as Product[]);
      return shuffled.slice(0, limit);
    },
  });
};

export const useAllProducts = () => {
  return useQuery({
    queryKey: ['allProducts'],
    queryFn: async () => {
      const response = await databases.listDocuments(
        databaseId,
        collectionId,
        [
          Query.limit(100),
          Query.select(['*', 'categories.*']), // Cambiado a categories.*
        ] as string[]
      );
      return response.documents as unknown as Product[];
    },
  });
};

export interface ProductFilters {
  categoryId?: string | null;
  gender?: 'female' | 'male' | 'unisex' | null;
  brand?: string | null;
  search?: string;
}

const CATEGORY_ID_CHUNK_SIZE = 100;

const chunkArray = <T,>(array: T[], size: number): T[][] => {
  const chunks: T[][] = [];
  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size));
  }
  return chunks;
};

/**
 * Appwrite no permite `Query.equal` de igualdad sobre atributos de relación
 * many-to-many desde el lado "parent" (products -> categories):
 * "400 Invalid query: Cannot query on virtual relationship attribute".
 *
 * Para filtrar por categoría resolvemos primero los IDs de producto de esa
 * categoría vía `getDocument` con `Query.select(['products.$id'])` sobre la
 * colección `categories` (relación manyToMany, twoWayKey `products`).
 */
const getProductIdsForCategory = async (categoryId: string): Promise<string[]> => {
  const category = await databases.getDocument(databaseId, 'categories', categoryId, [
    Query.select(['products.$id']) as unknown as string,
  ]);
  const products = (category as unknown as { products?: { $id: string }[] }).products ?? [];
  return products.map((p) => p.$id);
};

/**
 * Resuelve y cachea (por categoryId + baseFilters) el set completo de
 * productos de una categoría. Evita que `useInfiniteProducts` repita el
 * `getDocument` a `categories` y todos los chunks de `Query.equal('$id', ...)`
 * en cada página del scroll infinito.
 */
const useCategoryProducts = (categoryId: string | null | undefined, baseFilters: string[]) => {
  return useQuery({
    queryKey: ['categoryProducts', categoryId ?? null, baseFilters],
    enabled: Boolean(categoryId),
    queryFn: async () => {
      // `$id` no soporta URLs arbitrariamente largas (414 URI Too Long con
      // ~450 ids en una sola query), así que troceamos en lotes de 100.
      const productIds = await getProductIdsForCategory(categoryId as string);

      if (productIds.length === 0) {
        return [] as Product[];
      }

      const idChunks = chunkArray(productIds, CATEGORY_ID_CHUNK_SIZE);
      const chunkResponses = await Promise.all(
        idChunks.map((chunk) =>
          databases.listDocuments(databaseId, collectionId, [
            Query.equal('$id', chunk) as unknown as string,
            Query.limit(CATEGORY_ID_CHUNK_SIZE),
            Query.select(['*', 'categories.*']),
            ...baseFilters,
          ] as string[])
        )
      );

      return chunkResponses.flatMap((r) => r.documents) as unknown as Product[];
    },
  });
};

export const useInfiniteProducts = (pageSize: number = 12, filters?: ProductFilters) => {
  const { categoryId, gender, brand, search } = filters ?? {};

  const baseFilters: string[] = [];
  if (gender) baseFilters.push(Query.equal('gender', gender) as unknown as string);
  if (brand) baseFilters.push(Query.equal('fragrantica_brand', brand) as unknown as string);
  if (search && search.trim().length > 0) {
    baseFilters.push(Query.search('name', search.trim()) as unknown as string);
  }

  const categoryProductsQuery = useCategoryProducts(categoryId, baseFilters);

  return useInfiniteQuery({
    queryKey: [
      'infiniteProducts',
      pageSize,
      categoryId ?? null,
      gender ?? null,
      brand ?? null,
      search ?? '',
      categoryId ? categoryProductsQuery.data : null,
    ],
    queryFn: async ({ pageParam = 0 }) => {
      if (categoryId) {
        const merged = categoryProductsQuery.data ?? [];

        return {
          documents: merged.slice(pageParam, pageParam + pageSize),
          total: merged.length,
        };
      }

      const queries: string[] = [
        Query.offset(pageParam),
        Query.limit(pageSize),
        Query.select(['*', 'categories.*']),
        ...baseFilters,
      ] as string[];

      const response = await databases.listDocuments(databaseId, collectionId, queries);
      return {
        documents: response.documents as unknown as Product[],
        total: response.total,
      };
    },
    getNextPageParam: (lastPage, pages) => {
      const currentOffset = pages.length * pageSize;
      return currentOffset < lastPage.total ? currentOffset : undefined;
    },
    initialPageParam: 0,
    enabled: categoryId ? categoryProductsQuery.data !== undefined : true,
  });
};

export const useProduct = (slug: string | undefined) => {
  return useQuery({
    queryKey: ['product', slug],
    enabled: !!slug,
    queryFn: async () => {
      if (!slug) throw new Error('slug is required');
      const response = await databases.listDocuments(
        databaseId,
        collectionId,
        [
          Query.equal('slug', slug),
          Query.select(['*', 'categories.*']), // Cambiado a categories.*
        ] as string[]
      );
      if (response.documents.length === 0) throw new Error('Product not found');
      return response.documents[0] as unknown as Product;
    },
  });
};

export const useProductBrands = () => {
  return useQuery({
    queryKey: ['productBrands'],
    queryFn: async () => {
      const response = await databases.listDocuments(
        databaseId,
        collectionId,
        [
          Query.limit(500),
          Query.select(['fragrantica_brand']),
        ] as string[]
      );
      const brands = (response.documents as unknown as Pick<Product, 'fragrantica_brand'>[])
        .map((doc) => doc.fragrantica_brand)
        .filter((brand): brand is string => Boolean(brand));
      return Array.from(new Set(brands)).sort();
    },
  });
};
